import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { getFees } from '@/redux/modules/processingFee';
import { getInvoiceById } from '@/redux/modules/checkout/checkout.selectors';
import { OtherCharge } from '@/types/Invoice';
import { PaymentMethods, PaymentProviders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { useEffect, useState } from 'react';
import InvoiceSummaryListRow from './components/InvoiceSummaryListRow';

type Props = {
    activeProcessingFee: number; //cents
    bidderPaysProcessingFee: boolean;
    catalogId: number;
    currency: string;
    otherCharges: OtherCharge[];
    paymentMethod: PaymentMethods;
    paymentProvider: PaymentProviders;
    readOnly: boolean;
};

const InvoiceSummaryFeesList = ({
    activeProcessingFee,
    bidderPaysProcessingFee,
    catalogId,
    currency = 'USD',
    otherCharges = [],
    paymentMethod,
    paymentProvider,
    readOnly,
}: Props) => {
    const [processingFeesTotal, setProcessingFeesTotal] = useState(0);
    const isHighRisk = paymentProvider === PaymentProviders.Paysafe || paymentProvider === PaymentProviders.Payrix;

    const shippingQuote = useAppSelector(getCheckoutShippingQuote);
    const processingFees = useAppSelector((state) => getFees(state, catalogId));
    const { invoice } = useAppSelector((state) => getInvoiceById(state, catalogId, shippingQuote.shippingAvailable));

    //Note: Only *already paid* processing fees will ever populate otherCharges, they are calculated and returned after payment is made on Checkout.
    //Separate list of paid processing fees from other invoice alterations
    const paidProcessingFees = otherCharges.filter((row) => row.name === 'Processing fee');
    const otherAlterationsList = otherCharges
        .filter((row) => row.name !== 'Processing fee')
        .map((row) => {
            return (
                <div key={row.chargeID}>
                    <InvoiceSummaryListRow
                        amount={{ amount: row.amount, currency }}
                        label={row.name}
                    />
                </div>
            );
        });
    const hasPaidProcessingFees = paidProcessingFees.length > 0;

    useEffect(() => {
        const sumProcessingFees = () => {
            const processingFeeTotal = paidProcessingFees?.reduce(
                (runningTotal, currentFee) => runningTotal + currentFee.amount,
                0
            );
            if (!readOnly && activeProcessingFee > 0) {
                return processingFeeTotal + activeProcessingFee / 100;
            }
            return processingFeeTotal;
        };
        setProcessingFeesTotal(sumProcessingFees());
    }, [activeProcessingFee, paidProcessingFees, readOnly]);

    /*determines which fees to render based on whether the otherCharges array exists yet or not*/
    /* otherCharges comes off the invoice and includes the processing fee, but only exists after the invoice has been successfully paid,
        because prior to that the invoice is still in a flex state. Because the value of activeProcessingFee (comes from the catalog) needs
        shown prior to the invoice being paid, to avoid 2 processing fees from rendering after payment we have to use this condition*/
    return (
        <div>
            {otherAlterationsList}
            {bidderPaysProcessingFee && (
                <InvoiceSummaryListRow
                    amount={{
                        amount: hasPaidProcessingFees
                            ? processingFeesTotal
                            : invoice.highRisk
                              ? processingFees.hrFee / 100
                              : processingFees.ccFee / 100,
                        currency,
                    }}
                    infoText={isHighRisk && <FormattedMessage id="checkout.invoice_summary.processing_fee_popover" />}
                    label={<FormattedMessage id="checkout.invoice_summary.processing_fee" />}
                />
            )}
            {bidderPaysProcessingFee && paymentMethod === PaymentMethods.Ach && (
                <InvoiceSummaryListRow
                    amount={{
                        amount: invoice.highRisk
                            ? processingFees.achHrFee / 100 - processingFees.hrFee / 100
                            : processingFees.achFee / 100 - processingFees.ccFee / 100,
                        currency,
                    }}
                    infoText={isHighRisk && <FormattedMessage id="checkout.invoice_summary.processing_fee_popover" />}
                    label={<FormattedMessage id="checkout.invoice_summary.achDiscount" />}
                />
            )}
        </div>
    );
};

export default InvoiceSummaryFeesList;
